import { Component, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import {
  BaseDynamicReportFragment,
  CreateDynamicReportGQL,
  DynamicReportParametersInput,
  UpdateDynamicReportGQL,
} from '../../../generated/graphql.generated';
import { DetailsHelperService } from '../../services/details-helper.service';
import { MutateType } from '../../services/freya-mutate.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { MutateObjectComponent, MutateObjectElement } from '../mutate-object/mutate-object.component';

@Component({
  selector: 'app-mutate-dynamic-report',
  templateUrl: './mutate-dynamic-report.component.html',
  styleUrl: './mutate-dynamic-report.component.scss',
})
export class MutateDynamicReportComponent {
  @ViewChild('mutate') mutateRef: MutateObjectComponent;
  @ViewChild('name') nameRef: TemplateRef<any>;
  @ViewChild('description') descriptionRef: TemplateRef<any>;
  @ViewChild('path') pathRef: TemplateRef<any>;

  mutateType: MutateType;

  reportParameters: DynamicReportParametersInput;
  dynamicReport: BaseDynamicReportFragment;

  /**
   * Requirement: Feature 2 (Issue #5821) — Pre-fill path when creating from
   * within a folder context. Set via additionalValues[{ property: 'initialPath' }].
   */
  initialPath = '/';

  /**
   * Requirement: Feature 2 (Issue #5821) — Existing folder paths for autocomplete.
   * Set via additionalValues[{ property: 'existingPaths', value: allKnownPaths }]
   * from whichever parent opens this dialog.
   */
  existingPaths: string[] = ['/'];

  /** Filtered subset of existingPaths shown in the autocomplete dropdown. */
  filteredPaths: string[] = [];

  /**
   * Requirement: Zone restriction UX — set to true when the backend rejects an
   * update because the report belongs to a parent zone the user cannot edit from
   * their current zone. When true, all form controls are disabled and a warning
   * banner is displayed so the user understands they must switch zones.
   */
  notEditable = false;
  notEditableReason = '';

  steps: MutateObjectElement[];

  dynamicReportForm = new UntypedFormGroup({
    name: new UntypedFormControl(''),
    description: new UntypedFormControl(''),
    // Requirement: Feature 2 — path places the report in a folder hierarchy.
    path: new UntypedFormControl('/'),
  });

  constructor(
    private notify: FreyaNotificationsService,
    private createDynamicReportGQL: CreateDynamicReportGQL,
    private updateDynamicReportGQL: UpdateDynamicReportGQL,
    private detailsHelper: DetailsHelperService,
  ) {}

  mutateObject() {
    if (this.mutateType === 'create') {
      this.createDynamicReport();
    } else if (this.mutateType === 'update') {
      this.updateDynamicReport();
    }
  }

  setFormValues() {
    if (!this.dynamicReport) { return; }

    this.dynamicReportForm.reset({
      name: this.dynamicReport.name,
      description: this.dynamicReport.description,
      path: this.dynamicReport.path || '/',
    });
  }

  openDialog() {
    // Reset zone-restriction state each time the dialog is opened so a
    // previously blocked update doesn't carry over to a new dialog session.
    this.notEditable = false;
    this.notEditableReason = '';
    this.dynamicReportForm.enable();

    this.steps = [
      { name: 'Name', ref: this.nameRef, control: 'name', type: 'text' },
      { name: 'Description', ref: this.descriptionRef, control: 'description', type: 'text' },
      { name: 'Folder', ref: this.pathRef, control: 'path', type: 'text' },
    ];

    if (this.mutateType === 'create') {
      this.dynamicReportForm.reset({
        name: '',
        description: '',
        // Requirement: Pre-fill with current folder so the report is saved in
        // the right place when the user is browsing inside a subfolder.
        path: this.initialPath,
      });
    } else if (this.mutateType === 'update') {
      this.setFormValues();
    }

    this.mutateRef.steps = this.steps;
    this.mutateRef.mutateType = this.mutateType;
    this.mutateRef.openDialog();
  }

  /**
   * Requirement: Feature 2 (Issue #5821) — Filter existingPaths to match the
   * user's query string so the autocomplete dropdown shows relevant suggestions.
   * Called by (completeMethod) on the p-autoComplete in the path template.
   */
  filterPaths(event: { query: string }) {
    const query = (event.query || '').toLowerCase();
    if (!query) {
      this.filteredPaths = [...this.existingPaths];
    } else {
      this.filteredPaths = this.existingPaths.filter((p) =>
        p.toLowerCase().includes(query)
      );
    }
    // Always include the typed value itself so users can create new paths
    if (event.query && !this.filteredPaths.includes(event.query)) {
      this.filteredPaths = [event.query, ...this.filteredPaths];
    }
  }

  createDynamicReport() {
    const { value } = this.dynamicReportForm;
    this.createDynamicReportGQL
      .mutate({
        name: value.name,
        description: value.description || undefined,
        path: value.path || '/',
        parameters: this.reportParameters,
      })
      .subscribe({
        next: (res) => {
          this.mutateRef.closeDialog();
          this.dynamicReportForm.reset();
          this.notify.success('Revenue Report Created', 'Revenue Report has been created successfully');
          this.detailsHelper.pushUpdate({
            id: res.data.createDynamicReport,
            action: 'create',
            type: 'DynamicReport',
          });
        },
        error: (err) => {
          this.notify.error('Failed to create dynamic report', err.message);
          this.mutateRef.loading = false;
        },
      });
  }

  updateDynamicReport() {
    if (!this.dynamicReport) {
      this.notify.error('Failed to update dynamic report', 'Missing dynamic report data');
      this.mutateRef.loading = false;
      return;
    }

    const { value } = this.dynamicReportForm;

    this.updateDynamicReportGQL
      .mutate({
        reportId: this.dynamicReport.id,
        name: value.name,
        description: value.description || undefined,
        path: value.path || '/',
        parameters: this.reportParameters,
      })
      .subscribe({
        next: () => {
          this.mutateRef.closeDialog();
          this.notify.success('Revenue Report Updated', 'Revenue Report has been updated successfully');
          this.detailsHelper.pushUpdate({
            id: this.dynamicReport.id,
            action: 'update',
            type: 'DynamicReport',
          });
        },
        error: (err) => {
          // Requirement: Zone restriction UX — if the backend error indicates the
          // report belongs to a parent zone, disable the form and show a warning
          // banner instead of just a toast. This prevents the user from retrying
          // endlessly when the fix requires switching zones.
          const isZoneError =
            typeof err.message === 'string' &&
            err.message.includes('zone above your current zone');

          if (isZoneError) {
            this.notEditable = true;
            this.notEditableReason = err.message;
            this.dynamicReportForm.disable();
          }

          this.notify.error('Failed to update dynamic report', err.message);
          this.mutateRef.loading = false;
        },
      });
  }
}
